import "styles/questionnaire/newQuestionnaire.less";
import "styles/questionnaire/questionnaire.less";
import "styles/questionnaire/client.less";

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { message as antdMessage, Layout } from "antd";
import { AppContext } from "App";
import LottieAnimation from "components/basic/Spinner/Lottie";
import { ParentProfileType, StepType, QuestionType } from "enums/questionnaire";
import { useUser } from "hooks/common/useUser";
import useDebounce from "hooks/helpers/useDebounce";
import { useLocalStorage } from "hooks/helpers/useLocalStorage";
import { useDispatch } from "react-redux";
import { useAppSelector } from "redux/hooks";
import { selectAnswers, setAnswers } from "redux/reducers/answers";
import { selectArrayQuestionState } from "redux/reducers/arrayQuestion";
import { setBankAccounts } from "redux/reducers/bankAccounts";
import { selectDocument, setCurrentDocument } from "redux/reducers/document";
import { markCategoryInProgress, next, selectCurrentNavigation, selectLastNavigation, selectNavigationItems, setNavigationItems, setStepLoading } from "redux/reducers/navigation";
import { selectTaxProfile, setAllTaxProfiles, setIncompleteProfiles } from "redux/reducers/taxProfile";
import store from "redux/store";
import {
    useFetchConnectedBankAccountsQuery,
} from "services/banking";
import {
    useGetAllClientTaxProfilesQuery,
    useUpdateClientDocumentMutation,
    useGetChatwootHmacMutation,
} from "services/client";
import { findAndSetArrayPrev, findAndSetInnerArrayPrev, findAndSetNextStep, findAndSetPrevStep, findAndSetProfilePrev } from "utils/navigation";
import { calculateTotalProgress, checkDepends, createAnswersOBArray, findAllOBFiles, formatAnswers, getNavigationItems } from "utils/new-questionnaire";
import { logError } from "utils/sentry";

import DesktopHeader from "./components/Layout/DesktopHeader";
import Form from "./components/Layout/Form";
import MobileHeader from "./components/Layout/MobileHeader";
import SideNavigation from "./components/Layout/SideNavigation";
import { categories, Depend, Question, Step } from "./constants/questions";
const { Sider, Content, Header } = Layout;

const areAllVisibleRequiredQuestionsAnswered = (
    step: Step,
    categoryAnswers: Record<string, any>
): boolean => {
    if (!step.questions || step.questions.length === 0) return true;

    return step.questions.every(q => {
        // 🔹 1. Not visible = skip
        if (q.depend?.length && !checkDepends(q.depend, categoryAnswers)) return true;

        // 🔹 2. Not required = skip
        if (q.answerRequired === false) return true;

        // 🔹 3. DisallowedItems
        if (q.disallowedItems) {
            const ans = categoryAnswers?.[q.disallowedItems.question];
            const disallowedAnswers = [q.disallowedItems.answer];
            return !ans?.some((a: string) => disallowedAnswers.includes(a));
        }

        // 🔹 4. Nested Questions
        if (q.type === QuestionType.questionsList || q.type === QuestionType.conditionalQuestions) {
            const allAnswered = q.questions?.every(nestedQ => {
                if (nestedQ.answerRequired) {
                    const answer = categoryAnswers[nestedQ.question];
                    return !!answer;
                }
                return true;
            });
            return !!allAnswered;
        }

        // 🔹 5. Default
        const ans = categoryAnswers?.[q.question];

        return ans !== undefined && ans !== null && ans !== "";
    });
};

const Questionnaire: React.FC = () => {
    const siderStyle: React.CSSProperties = {
        overflow: "scroll",
        height: "calc(100vh - 96px)",
        position: "sticky",
        insetInlineStart: 0,
        top: 96,
    };
    const dispatch = useDispatch();
    const navigationItems = useAppSelector(selectNavigationItems);
    const { currentCategory, currentStep, currentQuestion, currentProfileQuestion, inProgressCategories, completedCategories } = useAppSelector(selectCurrentNavigation);
    const { lastCategory, lastStep } = useAppSelector(selectLastNavigation);
    const { isEditingArrayItem, isEditingInnerArrayItem } = useAppSelector(selectArrayQuestionState);
    const { answers } = useAppSelector(selectAnswers);
    const { isEditingProfile } = useAppSelector(selectTaxProfile);
    const { currentDocument, currentDocumentYear, selectedSharingPreference, taxService } = useAppSelector(selectDocument);
    const { isMobile } = useContext(AppContext);
    const { user } = useUser();
    const [getChatwootHmac] = useGetChatwootHmacMutation();

    const [updateClientDocument, { isLoading: isClientDocumentSaving }] = useUpdateClientDocumentMutation();
    const { data: connectedBankAccountsData, refetch: refetchConnectedBankAccounts } = useFetchConnectedBankAccountsQuery();

    const [pageLoading, setPageLoading] = useState(true);
    const [isAutosaving, setIsAutosaving] = useState(false);
    const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(true);
    const [autoSaveTrigger, setAutoSaveTrigger] = useState<Date | null>(null);

    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [totalProgress, setTotalProgress] = useLocalStorage<number>("new_questionnaire_total_progress", 0);

    const debouncedProgress = useDebounce(totalProgress, 100);

    const debouncedAutoSaveTrigger = useDebounce(autoSaveTrigger, 10000, currentDocumentYear); //Autosave when set answers with 10 seconds debounce

    const currentCategoryData = useMemo(() => {
        return categories[currentCategory] || {};
    }, [currentCategory]);

    const currentStepData = useMemo(() => {
        return currentCategoryData?.steps?.[currentStep] || {};
    }, [currentCategoryData, currentStep]);

    const currentQuestionData = useMemo(() => {
        return currentStepData?.questions?.[currentQuestion];
    }, [currentQuestion, currentStepData]);

    const { allProfilesCompleted, incompleteProfiles } = useMemo(() => {
        if (currentQuestionData?.type !== QuestionType.profileQuestion) {
            return { allProfilesCompleted: true, incompleteProfiles: [] };
        }

        const profilesAnswers = answers[currentCategoryData.title]?.[currentQuestionData.question] || [];
        const incompleteProfiles: { itemId: string; missingQuestions: string[] }[] = [];

        // ✅ Iterate through each profile and collect missing questions
        const profileCompletionStatus = profilesAnswers.map((profile: any, index: number) => {
            const formattedProfileAnswers = formatAnswers(profile.answers);
            const missingQuestions: string[] = [];

            currentQuestionData.questions?.forEach(profileQuestion => {
                if (profileQuestion.type === QuestionType.arrayQuestion) {
                    const answers = formattedProfileAnswers[profileQuestion.question];

                    if (answers?.some((answer: any) => !answer?.isCompleted)) {
                        missingQuestions.push(profileQuestion.id);
                    }
                } else {
                    const isRequired =
                        profileQuestion.answerRequired &&
                        (!profileQuestion.depend?.length || checkDepends(profileQuestion.depend, formattedProfileAnswers));

                    if (isRequired && formattedProfileAnswers[profileQuestion.question] === undefined) {
                        missingQuestions.push(profileQuestion.id);
                    }
                }
            });

            if (missingQuestions.length > 0) {
                incompleteProfiles.push({ itemId: profile.itemId, missingQuestions });
                console.warn(`🚨 Profile ${index + 1} (ID: ${profile.itemId}) is missing answers for:`, missingQuestions);
                return false; // Mark this profile as incomplete
            }

            return true; // Mark this profile as complete
        });

        // ✅ All profiles are completed if no profile is marked as `false`
        const allProfilesCompleted = profileCompletionStatus.every((status: any) => status);

        return { allProfilesCompleted, incompleteProfiles };
    }, [currentQuestionData, answers, currentCategoryData.title]);

    const { data: taxProfilesResponse, refetch: refetchTaxProfiles, status: taxProfilesStatus } = useGetAllClientTaxProfilesQuery(
        {
            client: user?.id!,
            parentType: currentStepData.parentProfileType! as ParentProfileType,
        },
        {
            skip: !user?.id || !currentStepData.parentProfileType,
        }
    );

    const connectedBankAccounts = useMemo(() => {
        return connectedBankAccountsData?.accDetails || [];
    }, [connectedBankAccountsData]);

    const handleAnswerChange = useCallback((question: string, answer: any) => {
        setAutoSaveTrigger(new Date());
        const newAnswers = {
            ...answers,
            [currentCategoryData.title]: {
                ...answers[currentCategoryData.title],
                [question]: answer,
            },
        };

        const categorySteps = categories.find(category => category.title === currentCategoryData.title)?.steps;
        categorySteps?.forEach((step: Step) => {
            // If any other category steps depend on the card swipe answer and dependency is not met, clear the answers
            if (step.depend && step.depend.some((depend: Depend) => depend.question === question) &&
                    !checkDepends(step.depend, newAnswers[currentCategoryData.title])) {
                step.questions?.forEach((question: Question) => {
                    if (newAnswers[currentCategoryData.title]?.[question.question]) {
                        newAnswers[currentCategoryData.title][question.question] = undefined;
                    }
                });
            }

            // If any other category step questions depend on the question and dependency is not met, clear the answers
            step.questions?.forEach((q: Question) => {
                if (q.depend && q.depend.some((depend: Depend) => depend.question === question) &&
                        !checkDepends(q.depend, newAnswers[currentCategoryData.title])) {
                    if (newAnswers[currentCategoryData.title]?.[q.question]) {
                        newAnswers[currentCategoryData.title][q.question] = undefined;
                    }
                }
            });
        });

        const allAnswered = areAllVisibleRequiredQuestionsAnswered(currentStepData, newAnswers[currentCategoryData.title]);

        setAllQuestionsAnswered(!!allAnswered);
        dispatch(setNavigationItems(getNavigationItems(newAnswers)));
        dispatch(setAnswers(newAnswers));
    }, [answers, currentCategoryData.title, currentStepData, dispatch]);

    const handleNext = useCallback(() => {
        const timerValue =
            currentStepData.type === StepType.categoryDone
                ? 1 : 800;

        dispatch(setStepLoading(true));
        const timer = setTimeout(() => {
            findAndSetNextStep(store.getState(), dispatch);
            dispatch(setStepLoading(false));
        }, timerValue);
        return () => clearTimeout(timer);
    }, [currentStepData, dispatch]);

    const handlePrev = useCallback(() => {
        if (isEditingInnerArrayItem) {
            findAndSetInnerArrayPrev(store.getState(), dispatch);
        } else if (isEditingArrayItem) {
            findAndSetArrayPrev(store.getState(), dispatch);
        } else if (isEditingProfile) {
            findAndSetProfilePrev(store.getState(), dispatch, currentStepData);
        } else {
            findAndSetPrevStep(store.getState(), dispatch);
        }
    }, [isEditingInnerArrayItem, isEditingArrayItem, isEditingProfile, dispatch, currentStepData]);

    const handleStepContinue = useCallback(() => {
        const isEligibleToContinue = (
            currentStepData.type === StepType.bankAccount ||
            currentStepData.type === StepType.categoryDone ||
            currentStepData.type === StepType.payment ||
            (currentStepData?.questions && (allQuestionsAnswered || (currentStepData.parentProfileType && allProfilesCompleted)))
        );

        if (isEligibleToContinue) {
            handleNext();
        }
    }, [
        currentStepData,
        allQuestionsAnswered,
        allProfilesCompleted,
        handleNext,
    ]);

    useEffect(() => {
        const handleBackButtonClick = (event: PopStateEvent) => {
            if (event.state.step === 1) {
                handlePrev();
                window.history.forward();
            }
        };

        // Push initial state to the history stack
        if (initialLoad) {
            window.history.pushState({ step: 1 }, "", window.location.pathname);
            window.history.pushState({ step: 2 }, "", window.location.pathname);
            setInitialLoad(false);
        }

        // Add popstate event listener
        window.addEventListener("popstate", handleBackButtonClick);

        // Cleanup on component unmount
        return () => {
            window.removeEventListener("popstate", handleBackButtonClick);
        };
    }, [handlePrev, currentCategory, currentStep, initialLoad]);

    useEffect(() => {
        if (!currentStepData?.questions?.length) return;

        const categoryAns = answers[currentCategoryData.title] ?? {};
        const allAnswered = areAllVisibleRequiredQuestionsAnswered(currentStepData, categoryAns);

        setAllQuestionsAnswered(allAnswered);
    }, [currentStepData, answers, currentCategoryData.title]);

    useEffect(() => {
        dispatch(setIncompleteProfiles(incompleteProfiles));
        if (incompleteProfiles.length > 0) {
            dispatch(markCategoryInProgress(categories[currentCategory].title));
        }
    }, [incompleteProfiles, dispatch, currentCategory]);

    useEffect(() => {
        setAutoSaveTrigger(new Date());

        if (navigationItems?.length) { // Safe check using optional chaining
            setTotalProgress(
                calculateTotalProgress(
                    categories.filter(cat => navigationItems.includes(cat.title)),
                    currentCategory,
                    currentStep
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCategory, currentStep]);

    useEffect(() => {
        if (
            debouncedProgress !== 100 &&
            debouncedProgress !== 0 &&
            user &&
            currentDocument.id &&
            (currentDocumentYear ? currentDocument.year === currentDocumentYear : true)
        ) {
            setIsAutosaving(true);
            const answersArray = createAnswersOBArray(answers, categories);
            const dataSections = findAllOBFiles(answers, categories);
            const data = {
                clientId: user?.id || "",
                year: currentDocumentYear,
                taxService: taxService,
                answersOB: {
                    convertedAnswers: answersArray,
                    answers: answers,
                    shouldComplete: true,
                    totalProgress: totalProgress,
                    currentCategory: currentCategory,
                    currentStep: currentStep,
                    currentQuestion: currentQuestion,
                    currentProfileQuestion: currentProfileQuestion,
                    lastCategory: lastCategory,
                    lastStep: lastStep,
                    currentGroupId: currentStep,
                    navigationItems: navigationItems,
                    completedCategories: completedCategories,
                    inProgressCategories: inProgressCategories,
                    saveDate: new Date(),
                    dataSections,
                    selectedSharingPreference: selectedSharingPreference,
                },
            };
            updateClientDocument({ documentId: currentDocument.id, data: data })
                .unwrap()
                .then(response => {
                    if (response) {
                        dispatch(setCurrentDocument(response.document));
                    }
                })
                .catch(async (e: any) => {
                    await antdMessage.error("Failed to save answers", e.message);
                    logError(e, {
                        function: "updateClientDocument",
                        client: user?.id,
                        currentDocument: currentDocument,
                        currentDocumentYear: currentDocumentYear,
                    });
                })
                .finally(() => {
                    setIsAutosaving(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAutoSaveTrigger]);

    useEffect(() => {
        if (taxProfilesResponse && taxProfilesResponse.taxProfiles && taxProfilesStatus === "fulfilled") {
            dispatch(setAllTaxProfiles(taxProfilesResponse.taxProfiles || []));
        }
    }, [taxProfilesResponse, taxProfilesStatus, dispatch, incompleteProfiles]);

    useEffect(() => {
        if (connectedBankAccounts.length > 0) {
            dispatch(setBankAccounts(connectedBankAccounts));
        }
    }, [connectedBankAccounts, dispatch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setNavigationItems(getNavigationItems(answers)));
            if (!completedCategories?.includes(categories[currentCategory].title) && !inProgressCategories?.includes(categories[currentCategory].title)) {
                dispatch(next({ nextCategory: 1, nextStep: 0, nextQuestion: 0, nextProfileQuestion: 0 }));
            }
            setPageLoading(false);
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!user?.id || !user?.info) return;
        // @ts-ignore
        const BASE_URL = process.env.REACT_APP_CHATWOOT_WEBSITE_URL;
        // @ts-ignore
        if (window.$chatwoot) return;

        // @ts-ignore
        window.chatwootSettings = {
            hideMessageBubble: true,
            position: "right",
            locale: "en",
            type: "standard",
            darkMode: "auto",
        };

        (function moveChatwootToMiddleRight() {
            if (typeof window === "undefined") return;

            const isDesktop = !isMobile;

            if (!isDesktop) return;
            const holder = document.getElementById("cw-widget-holder");
            if (holder) {
                holder.style.bottom = "auto";
                holder.style.top = "49%";
                holder.style.transform = "translateY(-50%)";
                holder.style.right = "20px";
            } else {
                setTimeout(moveChatwootToMiddleRight, 100);
            }
        })();

        const script = document.createElement("script");
        script.src = `${BASE_URL}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            // @ts-ignore
            window.chatwootSDK?.run({
                websiteToken: process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN,
                baseUrl: BASE_URL,
            });
        };

        document.body.appendChild(script);

        const onReady = async () => {
            if (!user?.info || !user.id) {
                console.warn("User info not ready");
                return;
            }

            const { hmac } = await getChatwootHmac({ clientId: user.id }).unwrap();

            // @ts-ignore
            if (!window.$chatwoot?.setUser) {
                console.error("Chatwoot not initialized");
                return;
            }

            // @ts-ignore
            window.$chatwoot.setUser(user.id, {
                name: `${user.info.firstName || ""} ${user.info.lastName || ""}`.trim(),
                email: user.info.email,
                identifier_hash: hmac,
            });

            // @ts-ignore
            window.$chatwoot.setCustomAttributes({
                signedUpAt: new Date().toISOString(),
                role: "client",
            });
        };

        window.addEventListener("chatwoot:ready", onReady);

        return () => {
            document.body.removeChild(script);
            window.removeEventListener("chatwoot:ready", onReady);
        };
    }, [getChatwootHmac, isMobile, user]);

    useEffect(() => {
        // @ts-ignore
        if (!window.$chatwoot?.setCustomAttributes) return;
        const currentQuestionId =
            currentQuestionData?.questions?.[currentProfileQuestion]?.question ??
            currentQuestionData?.question ??
            currentStepData?.description;

        const currentAnswer =
            currentQuestionId && answers?.[currentCategoryData?.title]?.[currentQuestionId as string];

        // Send live context
        // @ts-ignore
        window.$chatwoot.setCustomAttributes({
            step: currentStepData.title,
            category: currentCategoryData.title,
            question: currentQuestionId,
            answer: currentAnswer,
            updatedAt: new Date().toISOString(),
            pendingQuestion: "test",
            transactionId: null,
            totalAmount: null,
            description: null,
        });
    }, [currentCategoryData.title, currentStepData.title, currentQuestionData?.question, answers, currentStepData, currentQuestionData?.questions, currentProfileQuestion]);

    if (!user) {
        return null;
    }

    return (
        <div className="questionnaire">
            <LottieAnimation isLoading={pageLoading || (!isAutosaving && isClientDocumentSaving)} backgroundColor={"white"}/>

            <Layout className="questionnaire__layout">
                <Header
                    className="questionnaire__header"
                >
                    {isMobile
                        ? (<MobileHeader
                            user={user}
                            refetchConnectedBankAccounts={refetchConnectedBankAccounts}
                            handleContinue={handleStepContinue}
                            currentStepData={currentStepData}
                            totalProgress={totalProgress}
                        />) : (<DesktopHeader
                            user={user}
                            handleContinue={handleStepContinue}
                            currentStepData={currentStepData}
                            totalProgress={totalProgress}
                        />)}
                </Header>

                <Layout>
                    {!isMobile &&
                    <Sider width={285} theme="light" className="questionnaire__sider" style={siderStyle}>
                        <SideNavigation />
                    </Sider>}

                    <Layout className="questionnaire__main__layout">
                        <Content style={{ padding: 20 }}>
                            <Form
                                user={user}
                                refetchConnectedBankAccounts={refetchConnectedBankAccounts}
                                handlePrev={handlePrev}
                                answers={answers}
                                currentStepData={currentStepData}
                                currentCategoryTitle={currentCategoryData.title}
                                handleStepContinue={handleStepContinue}
                                handleAnswerChange={handleAnswerChange}
                                allQuestionsAnswered={allQuestionsAnswered}
                                allProfilesCompleted={allProfilesCompleted}
                                refetchTaxProfiles={refetchTaxProfiles}
                                currentCategoryData={currentCategoryData}
                                taxProfilesStatus={taxProfilesStatus}
                            />
                        </Content>
                    </Layout>
                </Layout>
            </Layout>
        </div>
    );
};

export default Questionnaire;
